

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    Chip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewOrderCancellationModal from 'components/NewOrderCancellationModal';

const OrderCancellations = () => {
    const [cancellations, setCancellations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('order_cancellations');

    useEffect(() => {
        fetchCancellations();
    }, []);

    const fetchCancellations = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/order-cancellations/');
            setCancellations(response.data);
        } catch (error) {
            console.error('Error fetching order cancellations:', error);
            setSnackbar({ open: true, message: 'Error loading order cancellations', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveCancellation = async (payload) => {
        try {
            await axiosInstance.post('/order-cancellations/', payload);
            setSnackbar({ open: true, message: 'Order Cancellation created successfully', severity: 'success' });
            fetchCancellations();
        } catch (error) {
            console.error('Error saving order cancellation:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/order-cancellation/${rowData.id}`);
    };

    const getStatusColor = (status) => {
        const statusMap = {
            'Approved': 'success',
            'Not Submitted': 'default',
            'Submitted': 'info',
            'Rejected': 'error',
            'Pending Approval': 'warning',
            'Approved-Not Processed': 'warning',
            'Processed': 'success'
        };
        return statusMap[status] || 'default';
    };

    const columns = [
        {
            Header: "Cancellation Name",
            accessor: "name",
            width: "15%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Travel Order",
            accessor: "travel_order_id",
            width: "12%",
            Cell: ({ row }) => row.original.travel_order?.name || row.original.travel_order_id
        },
        {
            Header: "Invoice",
            accessor: "invoice_id",
            width: "12%",
            Cell: ({ row }) => row.original.invoice?.invoice_number || row.original.invoice_id
        },
        {
            Header: "Order Line Item",
            accessor: "order_line_item_id",
            width: "12%",
            Cell: ({ row }) => row.original.order_line_item?.name || row.original.order_line_item_id
        },
        {
            Header: "Cancellation Type",
            accessor: "cancellation_type",
            width: "15%",
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Approval Status",
            accessor: "approval_status",
            width: "12%",
            Cell: ({ value }) => (
                <Chip
                    label={value || 'Not Submitted'}
                    size="small"
                    color={getStatusColor(value)}
                />
            )
        },
        {
            Header: "Status",
            accessor: "status",
            width: "10%",
            Cell: ({ value }) => (
                <Chip
                    label={value || 'Not Submitted'}
                    size="small"
                    color={getStatusColor(value)}
                />
            )
        },
        {
            Header: "Refund Amount",
            accessor: "refund_amount",
            width: "10%",
            Cell: ({ value }) => value ? `$${parseFloat(value).toFixed(2)}` : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: cancellations,
    }), [cancellations]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Order Cancellations</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Cancellation
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : cancellations.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Order Cancellation" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewOrderCancellationModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveCancellation}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default OrderCancellations;

